import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  AlertCircle,
  CalendarDays,
  Download,
  Loader2,
  RefreshCw,
  Search,
  Users,
  WalletCards,
} from "lucide-react";
import { toast } from "sonner";

import DataSourceNotice from "@/components/DataSourceNotice";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";


const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const localDate = () => {
  const value = new Date();
  const offset = value.getTimezoneOffset() * 60000;
  return new Date(value.getTime() - offset).toISOString().slice(0, 10);
};

const formatAmount = (value, currency, mixedCurrency = false) => {
  if (value === null || value === undefined || mixedCurrency) return "—";
  const amount = Number(value || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return currency && currency !== "N/A" ? `${amount} ${currency}` : amount;
};

const statusClass = (status) => {
  if (status === "Fully collected") return "border-emerald-200 bg-emerald-50 text-emerald-700";
  if (status === "Partially collected") return "border-amber-200 bg-amber-50 text-amber-700";
  return "border-slate-200 bg-slate-50 text-slate-600";
};

const SummaryCard = ({ label, value, icon: Icon, tone = "blue", note }) => {
  const tones = {
    blue: "bg-blue-50 text-blue-600",
    green: "bg-emerald-50 text-emerald-600",
    amber: "bg-amber-50 text-amber-600",
    violet: "bg-violet-50 text-violet-600",
    red: "bg-red-50 text-red-600",
  };
  return (
    <div className="rounded-lg border bg-card p-4 shadow-sm min-w-0">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
          <p className="mt-1 truncate text-xl font-bold" title={String(value)}>{value}</p>
          {note ? <p className="mt-1 text-[11px] text-muted-foreground">{note}</p> : null}
        </div>
        <span className={`rounded-lg p-2 ${tones[tone] || tones.blue}`}><Icon className="h-4 w-4" /></span>
      </div>
    </div>
  );
};

const DailyCollections = ({ companyCodes = [] }) => {
  const today = useMemo(localDate, []);
  const [companyCode, setCompanyCode] = useState("");
  const [collectionDate, setCollectionDate] = useState(today);
  const [search, setSearch] = useState("");
  const [currency, setCurrency] = useState("all");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [page, setPage] = useState(1);
  const autoLoaded = useRef(false);

  useEffect(() => {
    if (!companyCode && companyCodes.length > 0) {
      setCompanyCode(companyCodes[0].company_code);
    }
  }, [companyCode, companyCodes]);

  const validate = useCallback(() => {
    if (!companyCode.trim()) {
      toast.error("Select a company code");
      return false;
    }
    if (!collectionDate) {
      toast.error("Select a collection date");
      return false;
    }
    return true;
  }, [collectionDate, companyCode]);

  const loadData = useCallback(async (targetPage = 1) => {
    if (!validate()) return;
    setLoading(true);
    try {
      const response = await axios.get(`${API}/financial-agent/daily-collections`, {
        params: {
          company_code: companyCode.trim(),
          from_date: collectionDate,
          to_date: collectionDate,
          search: search.trim() || undefined,
          currency: currency === "all" ? undefined : currency,
          page: targetPage,
          page_size: 50,
        },
      });
      setData(response.data);
      setPage(targetPage);
    } catch (error) {
      console.error("Daily collection fetch failed", error);
      toast.error(error.response?.data?.detail || "Failed to load daily collections");
    } finally {
      setLoading(false);
    }
  }, [collectionDate, companyCode, currency, search, validate]);

  useEffect(() => {
    if (companyCodes.length > 0 && companyCode && !autoLoaded.current) {
      autoLoaded.current = true;
      loadData(1);
    }
  }, [companyCode, companyCodes.length, loadData]);

  const exportData = async () => {
    if (!validate()) return;
    setExporting(true);
    try {
      const response = await axios.get(`${API}/financial-agent/export/daily-collections-report`, {
        params: {
          company_code: companyCode.trim(),
          from_date: collectionDate,
          to_date: collectionDate,
          search: search.trim() || undefined,
          currency: currency === "all" ? undefined : currency,
        },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = `daily_collections_${companyCode}_${collectionDate}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Daily collection report downloaded");
    } catch (error) {
      console.error("Daily collection export failed", error);
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  const summary = data?.summary || {};
  const mixed = Boolean(summary.mixed_currency);
  const currencyLabel = summary.currency || "";
  const pagination = data?.pagination || { page: 1, pages: 0, total: 0 };

  return (
    <div className="space-y-4" data-testid="daily-collections-page">
      <DataSourceNotice source={data?.data_source} message={data?.message} />

      <div className="rounded-lg border bg-card p-3 shadow-xs">
        <div className="flex flex-wrap items-end gap-2.5">
          <div className="space-y-1">
            <label className="text-[11px] font-medium text-muted-foreground">Company</label>
            {companyCodes.length > 0 ? (
              <Select value={companyCode} onValueChange={(value) => { setCompanyCode(value); setData(null); }}>
                <SelectTrigger className="h-9 w-[210px] text-xs" data-testid="collection-company-select">
                  <SelectValue placeholder="Select Company" />
                </SelectTrigger>
                <SelectContent>
                  {companyCodes.map((company) => (
                    <SelectItem key={company.company_code} value={company.company_code}>
                      {company.company_code} - {company.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <Input className="h-9 w-[140px] text-xs" value={companyCode} onChange={(event) => setCompanyCode(event.target.value)} placeholder="e.g. 1000" />
            )}
          </div>
          <div className="space-y-1">
            <label className="text-[11px] font-medium text-muted-foreground">Collection date</label>
            <Input type="date" className="h-9 w-[150px] text-xs" value={collectionDate} onChange={(event) => setCollectionDate(event.target.value)} data-testid="collection-date" />
          </div>
          <div className="space-y-1 flex-1 min-w-[210px]">
            <label className="text-[11px] font-medium text-muted-foreground">Search customer or clearing document</label>
            <div className="relative">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input className="h-9 pl-8 text-xs" value={search} onChange={(event) => setSearch(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter") loadData(1); }} placeholder="Search..." />
            </div>
          </div>
          <div className="space-y-1">
            <label className="text-[11px] font-medium text-muted-foreground">Currency</label>
            <Select value={currency} onValueChange={setCurrency}>
              <SelectTrigger className="h-9 w-[130px] text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All currencies</SelectItem>
                {(data?.filter_options?.currencies || []).map((value) => <SelectItem key={value} value={value}>{value}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <Button className="h-9 bg-emerald-600 text-xs text-white hover:bg-emerald-700" onClick={() => loadData(1)} disabled={loading}>
            {loading ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}GO
          </Button>
          <Button variant="outline" className="h-9 text-xs" onClick={() => loadData(page)} disabled={loading} title="Refresh">
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
          <Button variant="outline" className="h-9 text-xs" onClick={exportData} disabled={exporting || !data}>
            {exporting ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Download className="mr-1 h-4 w-4" />}Export
          </Button>
        </div>
        <p className="mt-2 flex items-center gap-1.5 text-[11px] text-muted-foreground">
          <CalendarDays className="h-3.5 w-3.5" /> Opening is measured at the start of the selected date. Cash uses payment Posting Date; applied collections use SAP Clearing Date and a matching incoming-payment document.
        </p>
      </div>

      {mixed ? (
        <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          Monetary KPIs are not combined because the result contains multiple currencies. Select one currency to view totals.
        </div>
      ) : null}

      {data && data.classification?.complete === false ? (
        <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800">
          <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
          Some cleared receivables could not be matched to a configured incoming-payment document type. They are shown as unclassified clearing and are not included in Applied collections. Configured payment types: {(data.classification?.payment_document_types || []).join(", ") || "none"}.
        </div>
      ) : null}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
        <SummaryCard label="Opening outstanding" value={formatAmount(summary.opening_outstanding, currencyLabel, mixed)} icon={WalletCards} />
        <SummaryCard label="New invoices / debits" value={formatAmount(summary.new_invoices, currencyLabel, mixed)} icon={WalletCards} tone="violet" />
        <SummaryCard label="Cash received" value={formatAmount(summary.cash_received, currencyLabel, mixed)} icon={WalletCards} tone="green" />
        <SummaryCard label="Applied collections" value={formatAmount(summary.collections, currencyLabel, mixed)} icon={WalletCards} tone="green" />
        <SummaryCard label="Unapplied payments" value={formatAmount(summary.unapplied_payments, currencyLabel, mixed)} icon={WalletCards} tone="amber" />
        <SummaryCard label="Non-cash clearing" value={formatAmount(summary.non_cash_clearings, currencyLabel, mixed)} icon={WalletCards} tone="violet" />
        <SummaryCard label="Closing outstanding" value={formatAmount(summary.closing_outstanding, currencyLabel, mixed)} icon={WalletCards} tone="amber" />
        <SummaryCard label="Overdue outstanding" value={formatAmount(summary.overdue_outstanding, currencyLabel, mixed)} icon={AlertCircle} tone="red" />
        <SummaryCard label="Unclassified clearing" value={formatAmount(summary.unclassified_clearings, currencyLabel, mixed)} icon={AlertCircle} tone="red" />
        <SummaryCard label="Collection efficiency" value={summary.collection_efficiency === null || summary.collection_efficiency === undefined ? "—" : `${Number(summary.collection_efficiency).toFixed(2)}%`} icon={Users} tone="green" note={`${summary.customers_collected || 0} of ${summary.customers || 0} customers collected`} />
      </div>

      <div className="overflow-hidden rounded-lg border bg-card shadow-sm">
        <div className="flex items-center justify-between border-b px-4 py-3">
          <div>
            <h3 className="text-sm font-semibold">Customer collection reconciliation</h3>
            <p className="text-[11px] text-muted-foreground">Opening + new invoices/debits + other AR movements - applied collections = closing</p>
          </div>
          <Badge variant="outline">{pagination.total || 0} customers</Badge>
        </div>
        {loading ? (
          <div className="flex h-48 items-center justify-center text-sm text-muted-foreground"><Loader2 className="mr-2 h-5 w-5 animate-spin" />Loading SAP receivables...</div>
        ) : !data?.collections?.length ? (
          <div className="flex h-48 flex-col items-center justify-center px-4 text-center">
            <WalletCards className="mb-2 h-8 w-8 text-muted-foreground/60" />
            <p className="text-sm font-semibold">No collection records</p>
            <p className="mt-1 text-xs text-muted-foreground">Change the dates or filters, or review the SAP source status above.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[1720px] text-xs">
              <thead className="bg-muted/50 text-left text-muted-foreground">
                <tr>
                  <th className="px-3 py-2.5 font-medium">Customer</th>
                  <th className="px-3 py-2.5 font-medium">Name</th>
                  <th className="px-3 py-2.5 text-right font-medium">Opening</th>
                  <th className="px-3 py-2.5 text-right font-medium">New invoices</th>
                  <th className="px-3 py-2.5 text-right font-medium">Cash received</th>
                  <th className="px-3 py-2.5 text-right font-medium">Applied collections</th>
                  <th className="px-3 py-2.5 text-right font-medium">Unapplied</th>
                  <th className="px-3 py-2.5 text-right font-medium">Non-cash clearing</th>
                  <th className="px-3 py-2.5 text-right font-medium">Unclassified</th>
                  <th className="px-3 py-2.5 text-right font-medium">Other AR</th>
                  <th className="px-3 py-2.5 text-right font-medium">Closing</th>
                  <th className="px-3 py-2.5 text-right font-medium">Overdue</th>
                  <th className="px-3 py-2.5 text-right font-medium">Efficiency</th>
                  <th className="px-3 py-2.5 font-medium">Last collection</th>
                  <th className="px-3 py-2.5 font-medium">Status</th>
                </tr>
              </thead>
              <tbody>
                {data.collections.map((row) => (
                  <tr key={`${row.customer}-${row.currency}`} className="border-t hover:bg-muted/30">
                    <td className="px-3 py-2.5 font-mono font-medium">{row.customer}</td>
                    <td className="max-w-[220px] truncate px-3 py-2.5" title={row.customer_name}>{row.customer_name || "—"}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums">{formatAmount(row.opening_outstanding, row.currency)}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums">{formatAmount(row.new_invoices, row.currency)}</td>
                    <td className="px-3 py-2.5 text-right font-semibold tabular-nums text-emerald-700">{formatAmount(row.cash_received, row.currency)}</td>
                    <td className="px-3 py-2.5 text-right font-semibold tabular-nums text-emerald-700">{formatAmount(row.collections, row.currency)}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums text-amber-700">{formatAmount(row.unapplied_payments, row.currency)}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums text-violet-700">{formatAmount(row.non_cash_clearings, row.currency)}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums text-red-700">{formatAmount(row.unclassified_clearings, row.currency)}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums">{formatAmount(row.other_ar_movements, row.currency)}</td>
                    <td className="px-3 py-2.5 text-right font-semibold tabular-nums">{formatAmount(row.closing_outstanding, row.currency)}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums text-red-700">{formatAmount(row.overdue_outstanding, row.currency)}</td>
                    <td className="px-3 py-2.5 text-right tabular-nums">{Number(row.collection_efficiency || 0).toFixed(2)}%</td>
                    <td className="px-3 py-2.5">
                      <div>{row.last_collection_date || "—"}</div>
                      {row.last_clearing_document ? <div className="font-mono text-[10px] text-muted-foreground">{row.last_clearing_document}</div> : null}
                    </td>
                    <td className="px-3 py-2.5"><Badge variant="outline" className={statusClass(row.status)}>{row.status}</Badge></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        {pagination.pages > 1 ? (
          <div className="flex items-center justify-between border-t px-4 py-3 text-xs">
            <span className="text-muted-foreground">Page {pagination.page} of {pagination.pages}</span>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" disabled={loading || page <= 1} onClick={() => loadData(page - 1)}>Previous</Button>
              <Button variant="outline" size="sm" disabled={loading || page >= pagination.pages} onClick={() => loadData(page + 1)}>Next</Button>
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
};

export default DailyCollections;
